// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { useEffect, useCallback, useState } from 'react'
import { AddressFields } from '@/components/ui/AddressFields'
import { Input }         from '@/components/ui/Input'
import { Card }          from '@/components/ui/Card'
import { InfoBlock }     from '@/components/ui/InfoBlock'
import { useAutosave }   from '@/hooks/useAutosave'
import { getMasterKey, saveModuleProgress, getFirstProfile } from '@/services/profile'
import { db }            from '@/lib/db'
import { generateUUID, nowISO } from '@/lib/utils'
import { SyncStatus, WishCategory, CompletionStatus } from '@/types'
import { useAuthStore }  from '@/store'
import type { SaveStatus } from '@/hooks/useAutosave'

interface PersonalData {
  firstName: string; lastName: string; dateOfBirth: string
  email: string; phone: string
  street: string; houseNumber: string; houseNumberAddition: string
  postalCode: string; city: string
}
const EMPTY: PersonalData = {
  firstName: '', lastName: '', dateOfBirth: '',
  email: '', phone: '',
  street: '', houseNumber: '', houseNumberAddition: '',
  postalCode: '', city: '',
}
const DB_KEY = 'wizard_personal'

async function loadData(userId: string): Promise<PersonalData | null> {
  const rec = await db.wishes.where('[userId+category]').equals([userId, WishCategory.PERSONAL])
    .filter((r) => (r as unknown as { moduleKey?: string }).moduleKey === DB_KEY).first()
  if (!rec?.encryptedContent) return null
  try {
    const key = getMasterKey(userId)
    if (key && rec.blobIv) {
      const iv  = Uint8Array.from(atob(rec.blobIv), c => c.charCodeAt(0))
      const dec = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, rec.encryptedContent)
      return JSON.parse(new TextDecoder().decode(dec))
    }
    return JSON.parse(new TextDecoder().decode(rec.encryptedContent))
  } catch { return null }
}

// Haal profieldata op uit versleuteld profiel (masterKey al in memory na login)
async function loadFromProfile(userId: string): Promise<Partial<PersonalData> | null> {
  try {
    const profile = await getFirstProfile()
    if (!profile) return null
    const key = getMasterKey(userId)
    if (!key || !profile.encryptedProfile || !profile.blobIv) {
      // Alleen voornaam beschikbaar zonder decryptie
      return { firstName: profile.firstName }
    }
    const iv  = Uint8Array.from(atob(profile.blobIv), c => c.charCodeAt(0))
    const dec = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, profile.encryptedProfile)
    const decrypted = JSON.parse(new TextDecoder().decode(dec))
    // V35-P2: lees separate adresvelden als aanwezig (nieuwe gebruikers)
    // Fallback: extraheer postcode + stad uit legacy address-string (bestaande gebruikers)
    let street              = decrypted.street              ?? ''
    let houseNumber         = decrypted.houseNumber         ?? ''
    let houseNumberAddition = decrypted.houseNumberAddition ?? ''
    let postalCode          = ''
    let city                = ''

    if (street || houseNumber) {
      // Nieuwe gebruiker: separate velden aanwezig - lees postcode/city uit address
      const raw   = (decrypted.address ?? '').trim()
      // address formaat: "Straat 12, 1234 AB Amsterdam"
      // Zoek postcode-patroon voor extractie van postcode + stad
      const pcMatch = raw.match(/\b(\d{4}\s?[A-Z]{2})\s+(.+)$/)
      if (pcMatch) {
        postalCode = pcMatch[1].replace(' ', ' ')
        city       = pcMatch[2]
      }
    } else {
      // Bestaande gebruiker: geen separate velden - extraheer alles uit address-string
      const raw   = (decrypted.address ?? '').trim()
      const parts = raw.split(/\s+/)
      const singleToken = parts.length >= 1 && /^\d{4}[A-Z]{2}$/.test(parts[0])
      const twoToken    = parts.length >= 2 && /^\d{4}$/.test(parts[0]) && /^[A-Z]{2}$/.test(parts[1])
      if (singleToken) {
        postalCode = parts[0]
        city       = parts.slice(1).join(' ')
      } else if (twoToken) {
        postalCode = `${parts[0]} ${parts[1]}`
        city       = parts.slice(2).join(' ')
      } else {
        city = raw
      }
    }

    return {
      firstName:           profile.firstName ?? '',
      lastName:            decrypted.lastName ?? '',
      dateOfBirth:         decrypted.dateOfBirth ?? '',
      email:               decrypted.email ?? '',
      phone:               decrypted.phone ?? '',
      street,
      houseNumber,
      houseNumberAddition,
      postalCode,
      city,
    }
  } catch { return null }
}

async function persist(userId: string, data: PersonalData) {
  const enc = new TextEncoder().encode(JSON.stringify(data)).buffer
  const key = getMasterKey(userId)
  let blob: ArrayBuffer = enc, iv = ''
  if (key) {
    const ivArr = crypto.getRandomValues(new Uint8Array(12))
    blob = await crypto.subtle.encrypt({ name: 'AES-GCM', iv: ivArr }, key, enc)
    iv = btoa(String.fromCharCode(...ivArr))
  }
  // COMPLETE vereist: 7 kernvelden (straat/huisnummer zijn aanvullend, niet blokkerend)
  // IN_PROGRESS: >= 3 velden ingevuld
  const coreFields: (keyof PersonalData)[] = [
    'firstName', 'lastName', 'dateOfBirth', 'email', 'phone', 'postalCode', 'city',
  ]
  const filled = coreFields.filter(k => data[k].trim()).length
  const status = filled >= 3 ? CompletionStatus.IN_PROGRESS : CompletionStatus.NOT_STARTED
  const existing = await db.wishes.where('[userId+category]').equals([userId, WishCategory.PERSONAL])
    .filter((r) => (r as unknown as { moduleKey?: string }).moduleKey === DB_KEY).first()
  if (existing) {
    await db.wishes.update(existing.id, { encryptedContent: blob, blobIv: iv, completionStatus: status, lastUpdatedAt: nowISO(), syncStatus: SyncStatus.PENDING })
  } else {
    await db.wishes.add({ id: generateUUID(), userId, category: WishCategory.PERSONAL, sortOrder: 0, completionStatus: status, lastUpdatedAt: nowISO(), createdAt: nowISO(), syncStatus: SyncStatus.PENDING, cloudSyncId: null, encryptedContent: blob, blobIv: iv })
  }
  await saveModuleProgress(userId, 'intake', filled, 7)
}

interface Props { onSaveStatus: (s: SaveStatus) => void }

export function Step01PersonalData({ onSaveStatus }: Props) {
  const { userId } = useAuthStore()
  const [data,     setData]     = useState<PersonalData>(EMPTY)
  const [ready,    setReady]    = useState(false)
  const [prefilled, setPrefilled] = useState(false)

  useEffect(() => {
    if (!userId) return
    loadData(userId).then(async d => {
      if (d) {
        // Bestaande wizard-data laden - maar vul lege velden aan vanuit profiel
        // zodat velden die na onboarding zijn toegevoegd (zoals volledige naam) alsnog verschijnen
        const profileData = await loadFromProfile(userId)
        const merged: PersonalData = { ...d }
        if (profileData) {
          // Vul alleen lege velden aan - bestaande wizard-data wint altijd
          const keys: (keyof PersonalData)[] = ['firstName','lastName','dateOfBirth','email','phone','postalCode','city']
          for (const k of keys) {
            if (!merged[k] && profileData[k]) merged[k] = profileData[k] as string
          }
        }
        setData(merged)
      } else {
        // Geen wizard-data → prefill vanuit profiel
        const profileData = await loadFromProfile(userId)
        if (profileData) {
          setData(prev => ({ ...prev, ...profileData }))
          setPrefilled(true)
        }
      }
      setReady(true)
    })
  }, [userId])

  const save = useCallback(async (d: PersonalData) => { if (userId) await persist(userId, d) }, [userId])
  const { status } = useAutosave({ data, onSave: save, enabled: ready })
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  const upd = (k: keyof PersonalData, v: string) => setData(p => ({ ...p, [k]: v }))

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-xl font-semibold text-ink mb-1">Persoonlijke gegevens</h2>
        <p className="text-sm text-ink-3">Uw nabestaanden kunnen hiermee later snel de juiste instanties benaderen.</p>
      </div>

      {/* Prefill-melding */}
      {prefilled && (
        <InfoBlock title="Gegevens vooringevuld vanuit uw profiel" variant="neutral" defaultOpen={true}>
          <p>Uw naam, geboortedatum en contactgegevens zijn overgenomen uit uw profiel. Controleer en pas aan waar nodig.</p>
        </InfoBlock>
      )}

      <Card>
        <div className="flex flex-col gap-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="Voornaam" value={data.firstName} onChange={e => upd('firstName', e.target.value)} placeholder="Marianne" autoFocus required />
            <Input label="Achternaam" value={data.lastName} onChange={e => upd('lastName', e.target.value)} placeholder="de Vries" />
          </div>
          <Input label="Geboortedatum" type="date" value={data.dateOfBirth} onChange={e => upd('dateOfBirth', e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-2">
            <Input label="E-mailadres" type="email" value={data.email} onChange={e => upd('email', e.target.value)} placeholder="naam@voorbeeld.nl" />
            <Input label="Telefoonnummer" type="tel" value={data.phone} onChange={e => upd('phone', e.target.value)} placeholder="06 12 34 56 78" />
          </div>
          {/* Adres - V40: autofill op postcode + huisnummer */}
          <AddressFields
            values={{
              postalCode:          data.postalCode,
              houseNumber:         data.houseNumber,
              houseNumberAddition: data.houseNumberAddition,
              street:              data.street,
              city:                data.city,
            }}
            onChange={(field, value) => upd(field as keyof typeof data, value)}
            showAddition
          />
        </div>
      </Card>
    </div>
  )
}
